import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getLocalDateString } from "@/lib/date";

/**
 * AuthCallback — handles the `#session_id=...` URL fragment that
 * auth.emergentagent.com appends on its way back to our origin.
 *
 * The exchange MUST run exactly once (StrictMode double-invokes effects), so we
 * use a `useRef` guard rather than `useState` — per the Emergent Auth playbook.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh, setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const run = async () => {
      const hash = (typeof window !== "undefined" && window.location.hash) || "";
      const match = hash.match(/session_id=([^&]+)/);
      if (!match) {
        navigate("/login", { replace: true });
        return;
      }
      const sessionId = decodeURIComponent(match[1]);

      try {
        const res = await api.post("/auth/session", { session_id: sessionId });
        setUser(res.data);

        // One-shot migration: if this user has a local v2 snapshot from Fase 1.5,
        // upload it under today's date so they don't lose their in-progress day.
        try {
          const raw = window.localStorage.getItem("nexagenda:logros:v2");
          if (raw) {
            const parsed = JSON.parse(raw);
            // Phase 10: accept any legacy v2 snapshot with 1..50 slots.
            // The v2 schema always saved exactly 9, but we widen the guard
            // so a future forward-migrated snapshot is still eligible.
            if (Array.isArray(parsed) && parsed.length >= 1 && parsed.length <= 50) {
              const today = getLocalDateString();
              await api.put("/logros", { date: today, logros: parsed });
            }
          }
        } catch {
          /* migration is nice-to-have; never block login */
        } finally {
          try { window.localStorage.removeItem("nexagenda:logros:v1"); } catch {}
          try { window.localStorage.removeItem("nexagenda:logros:v2"); } catch {}
        }

        // Clean the URL hash before entering the app so a refresh doesn't re-trigger.
        if (window.history.replaceState) {
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
        }

        await refresh();
        navigate("/", { replace: true });
      } catch (e) {
        console.error("auth/session exchange failed", e);
        setErrorMsg("No pudimos iniciar sesión. Inténtelo de nuevo.");
        setTimeout(() => navigate("/login", { replace: true }), 1600);
      }
    };

    run();
  }, [navigate, refresh, setUser]);

  return (
    <div
      className="nx-page-bg min-h-screen w-full flex items-center justify-center px-6"
      data-testid="auth-callback"
    >
      <div className="flex flex-col items-center gap-4">
        <Loader2
          className="h-8 w-8 text-[#00E5FF] animate-spin [filter:drop-shadow(0_0_8px_rgba(0,229,255,0.5))]"
          strokeWidth={1.75}
        />
        <p className="text-[13px] text-[#9CA3AF]">
          {errorMsg || "Autenticando, Sr. Fernando…"}
        </p>
      </div>
    </div>
  );
}
